import {
  Eye,
  Mouse,
  BrainCircuit,
  Hammer,
  LayoutGrid,
  Medal,
  Users,
} from "lucide-react";
import "../styles/DigitalAccessibility.scss";

const DigitalAccessibility = () => {
  return (
    <section
      id="digital-accessibility"
      className="digital-accessibility"
      aria-labelledby="digital-accessibility-heading"
    >
      <div className="digital-accessibility__container">
        <header>
          <h2 id="digital-accessibility-heading" className="digital-accessibility__heading">
            About Digital Accessibility
          </h2>
        </header>

        <div className="digital-accessibility__content">
          {/* Why Digital Accessibility Matters */}
          <article className="digital-accessibility__card">
            <header>
              <h3 className="digital-accessibility__card-heading">
                Why Digital Accessibility Matters
              </h3>
            </header>
            <p className="digital-accessibility__card-text">
              Approximately 15-20% of the population has some form of disability
              or belongs to the senior demographic. By ensuring digital
              accessibility, you can extend your product's reach to a broader
              audience and improve your website's search engine rankings (SEO),
              which leads to increased visibility.
            </p>

            <h4 className="digital-accessibility__card-subheading">
              Key Benefits Include:
            </h4>
            <div className="digital-accessibility__benefits">
              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">
                  Improved Usability
                </h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <LayoutGrid className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Accessibility enhancements often lead to a more streamlined
                    and intuitive user experience for all visitors.
                  </p>
                </div>
              </div>

              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">
                  Positive Brand Perception
                </h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <Medal className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Demonstrating a commitment to inclusivity enhances your
                    brand's reputation, fostering trust and loyalty.
                  </p>
                </div>
              </div>

              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">
                  Higher Engagement
                </h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <Users className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Accessible websites promote greater interaction by making
                    content easier to navigate and understand for diverse user
                    groups, leading to improved conversion rates.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Understanding the EAA and WCAG */}
          <article className="digital-accessibility__card">
            <header>
              <h3 className="digital-accessibility__card-heading">
                Understanding the EAA and WCAG
              </h3>
            </header>
            <p className="digital-accessibility__card-text">
              The European Accessibility Act (EAA) is a directive that aims to
              improve accessibility across the EU, ensuring that products and
              services, including websites, are accessible to people with
              disabilities. Web Content Accessibility Guidelines (WCAG) provide
              specific recommendations to make web content perceivable,
              operable, understandable, and robust.
            </p>

            <h4 className="digital-accessibility__card-subheading">
              Examples of Digital Accessibility:
            </h4>
            <div className="digital-accessibility__benefits">
              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">
                  Perceivable Content
                </h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <Eye className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Alt text for images allows visually impaired users to
                    understand visual content through screen readers.
                  </p>
                </div>
              </div>

              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">Easy Navigation</h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <Mouse className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Keyboard-friendly design ensures people who cannot use a
                    mouse can still access all website functions.
                  </p>
                </div>
              </div>

              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">
                  Clear Instructions
                </h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <BrainCircuit className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Simple, direct error messages help users correct mistakes
                    without confusion or frustration.
                  </p>
                </div>
              </div>

              <div className="digital-accessibility__benefit">
                <h5 className="digital-accessibility__benefit-title">Robust</h5>
                <div className="digital-accessibility__benefit-body">
                  <div className="digital-accessibility__benefit-icon-wrapper">
                    <Hammer className="digital-accessibility__benefit-icon" aria-hidden="true" />
                  </div>
                  <p className="digital-accessibility__benefit-text">
                    Websites are built to work with various devices and
                    assistive technologies, ensuring accessibility for all.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default DigitalAccessibility; 